'use client';

/**
 * DataRoomChecklistOverlay Component
 *
 * Shows checklist progress for required documents in a data room.
 * Displays a progress bar, count text, and a collapsible list of missing items.
 */

import CheckCircle from '@mui/icons-material/CheckCircle';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Warning from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { iconSizes } from '../../theme/typography';
import { GlassButton } from '../GlassButton/GlassButton';

// ============================================================================
// Types
// ============================================================================

export interface DataRoomChecklistOverlayProps {
  /** Number of fulfilled checklist items */
  fulfilled: number;
  /** Total number of required checklist items */
  total: number;
  /** List of missing item labels */
  missingItems: string[];
}

// ============================================================================
// Component
// ============================================================================

export const DataRoomChecklistOverlay = ({
  fulfilled,
  total,
  missingItems,
}: DataRoomChecklistOverlayProps): React.ReactElement | null => {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);

  const progress = total > 0 ? (fulfilled / total) * 100 : 0;
  const isComplete = fulfilled >= total;

  // Don't render when there are no required documents to track
  if (total === 0) return null;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Stack spacing={1.5}>
        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={progress}
          color={isComplete ? 'success' : 'primary'}
          sx={{ height: 6, borderRadius: 3 }}
        />

        {/* Status text */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          {isComplete ? (
            <CheckCircle color="success" sx={{ fontSize: iconSizes.base }} />
          ) : (
            <Warning color="warning" sx={{ fontSize: iconSizes.base }} />
          )}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {isComplete
              ? t('dataRoom.checklist.allComplete')
              : t('dataRoom.checklist.progress', {
                  fulfilled: String(fulfilled),
                  total: String(total),
                })}
          </Typography>
        </Stack>

        {/* Missing items (collapsible) */}
        {!isComplete && missingItems.length > 0 && (
          <>
            <GlassButton
              variant="ghost"
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            >
              {t('dataRoom.checklist.missingItems', {
                count: String(missingItems.length),
              })}
            </GlassButton>

            <Collapse in={expanded}>
              <List dense disablePadding>
                {missingItems.map((item) => (
                  <ListItem key={item} sx={{ py: 0.25, px: 1 }}>
                    <ListItemText
                      primary={item}
                      slotProps={{
                        primary: {
                          variant: 'body2',
                          color: 'textSecondary',
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </Stack>
    </Box>
  );
};

DataRoomChecklistOverlay.displayName = 'DataRoomChecklistOverlay';

export default DataRoomChecklistOverlay;
